import React, { ReactElement, useContext, useMemo } from 'react';
import { Button, Dropdown, Menu, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ControlOutlined } from '@ant-design/icons';
import ActionTypes from '@utils/deviceActions';
import roles from '@utils/rolesConstants';
import SessionContext from '@providers/Session/SessionContext';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import {
  motProducts,
  ApplicationsConstants,
} from '@utils/applicationsConstants';

interface IDeviceActionsMenuProps {
  button?: ReactElement;
  loading?: boolean;
  onActionClick?: (key: {
    key: ActionTypes;
    keyPath: React.Key[];
    item: React.ReactInstance;
    domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
  }) => void;
  className?: string;
  showContracted?: string;
  disabled?: boolean;
  isUpload?: boolean;
  isStock?: boolean;
}
const DeviceActionsMenu: React.FC<IDeviceActionsMenuProps> = ({
  button,
  onActionClick,
  className,
  disabled,
  isUpload,
  isStock = false,
  loading = false,
  showContracted = 'true',
}) => {
  const { t } = useTranslation('device-actions');
  const { keycloak } = useContext(KeycloakContext);
  const { isRoot, effectiveRoles, selectedApp, isMot } =
    useContext(SessionContext);
  const rolesToAccess = effectiveRoles?.portal;
  const customerTypeLogged = keycloak.idTokenParsed?.customerType;

  const menuItems = useMemo(() => {
    const defaultMenu = [
      {
        actionType: ActionTypes.NETWORK_RESET,
        role: roles.NETWORK_RESET,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.CONTRACT_MOVEMENT,
        role: roles.CONTRACT_MOVEMENT,
        products: [
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: false,
      },
      {
        actionType: ActionTypes.TRANSFER,
        role: roles.TRANSFER,
        products: [
          ApplicationsConstants.MOT_ENTERPRISE,
          ApplicationsConstants.MOT_BUSINESS,
        ],
        rootCanSee: false,
      },
      {
        actionType: ActionTypes.OPERATOR_RESTRICTION,
        role: roles.OPERATOR_RESTRICTION,
        products: [ApplicationsConstants.MULTI_CONNECT],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.SIMCARD_RESET,
        role: roles.SIMCARD_RESET,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.NICKNAME_CHANGE,
        role: roles.NICKNAME_CHANGE,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.MSISDN_EXCHANGE,
        role: roles.MSISDN_EXCHANGE,
        products: motProducts,
        rootCanSee: false,
      },
      {
        actionType: ActionTypes.PLASTIC_EXCHANGE,
        role: roles.PLASTIC_EXCHANGE,
        products: motProducts,
        rootCanSee: false,
      },
      {
        actionType: ActionTypes.PRIORITY_OPERATOR,
        role: roles.PRIORITY_CHANGE,
        products: [ApplicationsConstants.MULTI_CONNECT],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.PRIORITY_OPERATOR,
        role: roles.PRIORITY_CHANGE_CONNECT_ONE,
        products: [ApplicationsConstants.CONNECT_ONE],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.SMS_BLOCK,
        role: roles.SMS_BLOCK,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.NETWORK_BLOCK,
        role: roles.NETWORK_BLOCK_UNLOCK,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.CHANGE_APN,
        role: roles.CHANGE_APN,
        products: [
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
          ...motProducts,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.SYNC_APN,
        role: roles.SYNC_APN,
        products: [
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
          ...motProducts,
        ],
        rootCanSee: true,
        isOnlyUpload: true,
      },
      {
        actionType: ActionTypes.SEND_SMS,
        role: roles.SEND_SMS,
        products: [...motProducts],
        whoCanSee: ['BROKER', 'CUSTOMER', 'SUPERBROKER'],
        rootCanSee: false,
      },
      {
        actionType: ActionTypes.CONTRACT_SUSPEND,
        role: roles.CONTRACT_SUSPEND,
        products: [
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.SUSPENSION,
        role: roles.SUSPENSION,
        products: motProducts,
        rootCanSee: true,
        whoCanSee: ['BROKER', 'CUSTOMER'],
      },
      {
        actionType: ActionTypes.BROKER_CANCELLATION,
        role: roles.BROKER_CANCELLATION,
        products: motProducts,
        whoCanSee: ['CUSTOMER'],
      },
      {
        actionType: ActionTypes.SUSPENSION,
        role: roles.SUSPENSION_WITHDRAWAL,
        products: motProducts,
        whoCanSee: ['CUSTOMER'],
      },
      {
        actionType: ActionTypes.HLR_REPROG,
        role: roles.HLR_REPROG,
        products: [
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.IMEI_LOCK,
        role: roles.IMEI_LOCK_UNLOCK,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.ALGAR_BAIT_RENEWAL,
        role: roles.ALGAR_BAIT_RENEWAL,
        products: [
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.CANCELLATION,
        role: roles.CANCELLATION,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
      {
        actionType: ActionTypes.CHANGE_SIMCARD,
        role: roles.CHANGE_SIMCARD,
        products: [
          ...motProducts,
          ApplicationsConstants.MULTI_CONNECT,
          ApplicationsConstants.CONNECT_ONE,
        ],
        rootCanSee: true,
      },
    ];

    if (isStock) {
      defaultMenu.push({
        actionType: ActionTypes.MOT_DEACTIVATION,
        role: roles.MOT_DEACTIVATION,
        products: [
          ApplicationsConstants.MOT_ENTERPRISE,
          ApplicationsConstants.MOT_BUSINESS,
        ],
        whoCanSee: ['SUPERBROKER', 'BROKER'],
        rootCanSee: false,
      });
    }

    if (showContracted !== 'true') {
      defaultMenu.push(
        {
          actionType: ActionTypes.FORCE_REACTIVATION,
          role: roles.FORCE_REACTIVATION,
          products: motProducts,
          rootCanSee: false,
        },
        {
          actionType: ActionTypes.ADD_CREDIT_TO_SIMCARD,
          role: roles.ADD_CREDIT_TO_SIMCARD,
          products: [
            ApplicationsConstants.MOT_ENTERPRISE,
            ApplicationsConstants.MOT_BUSINESS,
          ],
          rootCanSee: false,
        },
        {
          actionType: ActionTypes.MANUAL_ACTIVATION,
          role: roles.MANUAL_ACTIVATION,
          products: [
            ApplicationsConstants.MOT_ENTERPRISE,
            ApplicationsConstants.MOT_BUSINESS,
          ],
          rootCanSee: false,
        },
      );
    }
    return defaultMenu
      .filter(item => {
        // @ts-ignore
        const isOnRightProduct = item.products.includes(selectedApp);
        const havePermission = rolesToAccess?.includes(item.role);
        const canSee = !item.whoCanSee
          ? true
          : item.whoCanSee?.includes(`${customerTypeLogged}`);

        return isOnRightProduct && havePermission && canSee;
      })
      ?.map(item => ({ ...item, label: t(`menu.${item.actionType}`) }));
  }, [
    isStock,
    showContracted,
    selectedApp,
    rolesToAccess,
    customerTypeLogged,
    t,
  ]);

  menuItems?.sort((a, b) => a.label.localeCompare(b.label));

  const menu = (
    <Menu
      inlineIndent={8}
      onClick={({ key, keyPath, item, domEvent }) => {
        if (onActionClick) {
          onActionClick({
            key: Number(key) as ActionTypes,
            keyPath,
            item,
            domEvent,
          });
        }
      }}
      style={{ padding: '10px' }}
    >
      {menuItems.length === 0 ? (
        <div style={{ padding: '10px' }}>{t('no-actions')}</div>
      ) : (
        <>
          {menuItems.length > 0 && (
            <Menu.ItemGroup key="g1">
              {menuItems.map(item =>
                (!item.rootCanSee && isMot && isRoot) ||
                (item?.isOnlyUpload && !isUpload) ? (
                  <Tooltip key={item.role} title={t('root-cannot-see')}>
                    <Menu.Item key={item.actionType} disabled>
                      {item.label}
                    </Menu.Item>
                  </Tooltip>
                ) : (
                  <Menu.Item key={item.actionType}>{item.label}</Menu.Item>
                ),
              )}
            </Menu.ItemGroup>
          )}
        </>
      )}
    </Menu>
  );

  return (
    <Dropdown
      overlayClassName="overlay-menu"
      placement="bottomRight"
      overlay={menu}
      className={className}
      trigger={['click']}
      disabled={disabled}
    >
      <div>
        {button || (
          <Tooltip title={t('actions')}>
            <Button icon={<ControlOutlined />} loading={loading} />
          </Tooltip>
        )}
      </div>
    </Dropdown>
  );
};

export default DeviceActionsMenu;
